import { VVDN_BRAND } from '../config/brand'
import { DEMO_WIRELESS_APS } from '../config/demoDevices'

const FLOORPLAN_HOST = 'https://floormap.vvdncloud.com'
const API = (process.env.REACT_APP_FLOORPLAN_API || FLOORPLAN_HOST).replace(/\/$/, '')

function sessionHeaders(orgId: string, token?: string | null, locationId?: string | null) {
  const headers: Record<string, string> = {
    'X-Org-Id': orgId,
    'X-Location-Id': locationId ?? VVDN_BRAND.siteId,
  }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function apiError(res: Response, fallback: string) {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.message) msg = String(body.message)
  } catch {
    /* ignore */
  }
  throw new Error(msg)
}

export async function fetchNetworkingFloors(orgId: string, token?: string | null) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors`, { headers: sessionHeaders(orgId, token) })
  if (!res.ok) return { data: [] }
  return res.json()
}

export async function createNetworkingFloor(
  orgId: string,
  token: string | null | undefined,
  body: Record<string, unknown>,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors`, {
    method: 'POST',
    headers: { ...sessionHeaders(orgId, token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await apiError(res, 'Create floor failed')
  return res.json()
}

export async function fetchNetworkingFloorplan(orgId: string, token?: string | null, floorId?: string | null) {
  const q = floorId ? `?floorId=${encodeURIComponent(floorId)}` : ''
  const res = await fetch(`${API}/api/v1/networking/wireless/floorplan${q}`, {
    headers: sessionHeaders(orgId, token),
  })
  if (!res.ok) return { accessPoints: [] }
  return res.json()
}

export async function placeNetworkingFloorAp(
  floorId: string,
  orgId: string,
  token: string | null | undefined,
  body: Record<string, unknown>,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/access-points`, {
    method: 'POST',
    headers: { ...sessionHeaders(orgId, token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await apiError(res, 'Place AP failed')
  return res.json()
}

export async function updateNetworkingFloorAp(
  floorId: string,
  apId: string,
  orgId: string,
  token: string | null | undefined,
  body: Record<string, unknown>,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/access-points/${apId}`, {
    method: 'PATCH',
    headers: { ...sessionHeaders(orgId, token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await apiError(res, 'Update AP position failed')
  return res.json()
}

export async function removeNetworkingFloorAp(
  floorId: string,
  apId: string,
  orgId: string,
  token?: string | null,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/access-points/${apId}`, {
    method: 'DELETE',
    headers: sessionHeaders(orgId, token),
  })
  if (!res.ok) await apiError(res, 'Remove AP failed')
}

export async function updateNetworkingFloor(
  floorId: string,
  orgId: string,
  token: string | null | undefined,
  body: Record<string, unknown>,
  locationId?: string | null,
) {
  const headers = { ...sessionHeaders(orgId, token, locationId), 'Content-Type': 'application/json' }
  const payload = JSON.stringify(body)
  let res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/settings`, {
    method: 'POST',
    headers,
    body: payload,
  })
  if (res.status === 404 || res.status === 405) {
    res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}`, {
      method: 'PATCH',
      headers,
      body: payload,
    })
  }
  if (!res.ok) await apiError(res, 'Update floor failed')
  return res.json()
}

export async function saveNetworkingFloorGeometry(
  floorId: string,
  orgId: string,
  token: string | null | undefined,
  geometry: Record<string, unknown>,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/geometry`, {
    method: 'POST',
    headers: { ...sessionHeaders(orgId, token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ geometry }),
  })
  if (!res.ok) await apiError(res, 'Save floor geometry failed')
  return res.json()
}

export async function deleteNetworkingFloor(floorId: string, orgId: string, token?: string | null) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}`, {
    method: 'DELETE',
    headers: sessionHeaders(orgId, token),
  })
  if (!res.ok) await apiError(res, 'Delete floor failed')
}

export async function fetchDevices(orgId: string, token?: string | null) {
  const res = await fetch(`${API}/api/v1/devices?page=0&size=500`, { headers: sessionHeaders(orgId, token) })
  if (!res.ok) return { data: [...DEMO_WIRELESS_APS] }
  const json = await res.json()
  const data = json?.data
  if (!Array.isArray(data) || data.length === 0) return { data: [...DEMO_WIRELESS_APS] }
  return json
}

export async function runClientSpeedTest(
  floorId: string,
  clientId: string,
  orgId: string,
  token: string | null | undefined,
  rssi: number,
) {
  const res = await fetch(`${API}/api/v1/networking/wireless/floors/${floorId}/clients/${encodeURIComponent(clientId)}/speedtest`, {
    method: 'POST',
    headers: { ...sessionHeaders(orgId, token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ rssi }),
  })
  if (!res.ok) await apiError(res, 'Speed test failed')
  return res.json()
}

export async function fetchAlerts(
  orgId: string,
  token?: string | null,
  _opts?: { status?: string },
) {
  const res = await fetch(`${API}/api/v1/alerts?status=FIRING`, { headers: sessionHeaders(orgId, token) })
  if (!res.ok) return { data: [] }
  return res.json()
}
